import { useEffect, useRef, useState } from "react";

export const EVENT_TYPES: Record<string, string> = {
  DAY_DOWN_BREAKTHROUGH: "Day Down Breakthrough",
  DAY_UP_BREAKTHROUGH: "Day Up Breakthrough",
  DOWN_1: "Small Fall",
  DOWN_2: "Mid Fall",
  DOWN_3: "High Fall",
  HIGH_VOLUME_RISE_1: "Small High Volume Rise",
  HIGH_VOLUME_RISE_2: "Mid High Volume Rise",
  MONTH_DOWN_BREAKTHROUGH: "Month Down Breakthrough",
  PULLBACK: "Pullback",
  RALLY: "Rally",
  UP_1: "Small Rise",
  UP_2: "Mid Rise",
  UP_3: "High Rise",
  WEEK_DOWN_BREAKTHROUGH: "Week Down Breakthrough",
  WEEK_UP_BREAKTHROUGH: "Week Up Breakthrough",
};

export const NOTICE_TYPES = {
  PRICE_AND_VOLUME: "Price and Volume",
  PRICE_BREAKTHROUGH: "Price Breakthrough",
  PRICE_CHANGE: "Price Change",
  PRICE_FLUCTUATION: "Price Fluctuation",
};

export interface ITopMover {
  id: number;
  createTimestamp: number;
  eventType: string;
  noticeType: string;
  period: string;
  openTime: number;
  priceChange: string;
  symbol: string;
  updateTimestamp: number | null;
}

interface TopMoverWSData {
  s: string; // symbol
  E: number; // event time
  i: number; // id
  n: string; // notice type
  e: string; // event type
  P: string; // period
  p: string; // price change
  o: number; // open time
  c: number; // close time
}

interface TopMoverWS {
  stream: string;
  data: TopMoverWSData;
}

const REST_URL = "https://www.binance.com/fapi/v1/topMovers";
const WS_URL = "wss://fstream.binance.com/stream";

export const useBinanceTop = () => {
  const [topMovers, setTopMovers] = useState<ITopMover[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const ws = useRef<WebSocket | null>(null);

  const fetchTopMovers = async () => {
    try {
      const response = await fetch(REST_URL);
      const data: ITopMover[] = await response.json();
      // Filter out USDC assets
      const filteredData = data.filter((item) => !item.symbol.endsWith("USDC"));
      filteredData.sort((a, b) => Number(b.id) - Number(a.id));
      setTopMovers(filteredData);
    } catch (error) {
      console.error("Error fetching top movers:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWSMessage = (message: TopMoverWS) => {
    if (message.stream === "topMovers") {
      const wsData = message.data;

      // Skip USDC assets
      if (wsData.s.endsWith("USDC")) {
        return;
      }

      setTopMovers((prev) => {
        // Find if we already have this symbol
        const index = prev.findIndex((item) => item.symbol === wsData.s);
        const newMover: ITopMover = {
          id: wsData.i,
          createTimestamp: wsData.E,
          eventType: wsData.e,
          noticeType: wsData.n,
          period: wsData.P,
          openTime: wsData.o,
          priceChange: wsData.p,
          symbol: wsData.s,
          updateTimestamp: wsData.c,
        };

        if (index === -1) {
          return [...prev, newMover];
        }

        const updated = [...prev];
        updated[index] = newMover;
        return updated.sort((a, b) => Number(a.id) - Number(b.id));
      });
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchTopMovers();

    // Setup WebSocket
    ws.current = new WebSocket(WS_URL);

    ws.current.onopen = () => {
      if (ws.current?.readyState === WebSocket.OPEN) {
        ws.current.send(
          JSON.stringify({
            method: "SUBSCRIBE",
            params: ["topMovers"],
            id: 11,
          }),
        );
      }
    };

    ws.current.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        handleWSMessage(message);
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    ws.current.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    // Cleanup
    return () => {
      if (ws.current) {
        if (ws.current.readyState === WebSocket.OPEN) {
          ws.current.send(
            JSON.stringify({
              method: "UNSUBSCRIBE",
              params: ["topMovers"],
              id: 1,
            }),
          );
        }
        ws.current.close();
      }
    };
  }, []);

  return { data: topMovers, isLoading };
};
